import type { ComponentProps } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  BarChart3,
  ClipboardCheck,
  ClipboardList,
  Inbox,
  ListChecks,
  QrCode,
  Settings,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useMyStats, useMyTasks, useSubmissions } from '@/lib/queries'
import { Badge, Button, Card, CardTitle, EmptyState, StatTile } from '@/components/ui'
import { Leaderboard } from '@/components/Leaderboard'
import { Hint } from '@/components/Hint'
import { formatNumber, formatPercent, formatRelative } from '@/lib/utils'
import { moduleEnabled } from '@/lib/modules'
import { useI18n, useT } from '@/i18n/I18nProvider'
import type { MyNumbers, Submission, User } from '@/types/api'

/**
 * Two dashboards, chosen by role.
 *
 * The two audiences want opposite things from this screen. A worker opens the
 * app standing next to a machine, with one job — report what they are looking
 * at — and everything else on the page is in the way. A supervisor opens it at
 * a desk to answer "what needs me today", which is a queue, not a button.
 *
 * Showing both groups the union of both screens meant the worker scrolled past
 * a manager's open-queue to reach the report button, and the supervisor's most
 * important number sat under four tiles about their own submissions, of which
 * they file almost none.
 */
/** Twelve months back, as a date string. */
function aYearAgo(): string {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 1)

  return date.toISOString().slice(0, 10)
}

/*
 * Both dashboards drop what the company does not have.
 *
 * Home is the one screen every company keeps whatever is switched off, so it
 * cannot be guarded like the others — it has to shrink instead. A report
 * button, a queue or a 5S tile for a module that is off would each lead to
 * "not switched on", which is a dead end on the screen people open first.
 */
function modulesOf(user: User) {
  const modules = user.company?.modules

  return {
    submissions: moduleEnabled(modules, 'submissions'),
    fiveS: moduleEnabled(modules, 'fiveS'),
    analytics: moduleEnabled(modules, 'analytics'),
  }
}

export function HomePage() {
  const { user } = useAuth()

  if (!user) return null

  return user.isManager ? <ManagerHome user={user} /> : <WorkerHome user={user} />
}

// ---------------------------------------------------------------------------
// Worker
// ---------------------------------------------------------------------------

/**
 * Deliberately small.
 *
 * Two actions, their own numbers, their own tasks, their own recent reports.
 * No queue, no analytics, no work-centre breakdowns — a worker can act on none
 * of it, and every row of it is a row between them and the report button.
 */
function WorkerHome({ user }: { user: User }) {
  const t = useT()
  const has = modulesOf(user)
  const stats = useMyStats()
  const tasks = useMyTasks(true, { enabled: has.submissions })
  const recent = useSubmissions({ perPage: 5 }, { enabled: has.submissions })

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold sm:text-2xl">
          {t('home.greeting', { name: user.name.split(' ')[0] })}
        </h1>
        <p className="muted text-sm">{user.roleLabel}</p>
      </header>

      {/* The two things a worker actually came here to do, at the top and big
          enough to hit with a glove on. */}
      <section className="grid gap-3 sm:grid-cols-2">
        {has.submissions && (
          <BigAction
            to="/prijave/nova"
            icon={<ListChecks className="size-6" />}
            title={t('home.report.title')}
            text={t('home.report.text')}
            primary
          />
        )}
        <BigAction
          to="/skeniraj"
          icon={<QrCode className="size-6" />}
          title={t('home.scan.title')}
          text={t('home.scan.text')}
        />
      </section>

      {has.submissions && (
        <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <ExplainedTile
            label={t('home.stat.mySubmissions')}
            value={formatNumber(stats.data?.total ?? 0)}
            explain={t('home.stat.mySubmissions.explain')}
          />
          <ExplainedTile
            label={t('home.stat.inProgress')}
            value={formatNumber(stats.data?.open ?? 0)}
            explain={t('home.stat.inProgress.explain')}
          />
          <ExplainedTile
            label={t('home.stat.acceptedProposals')}
            value={formatNumber(stats.data?.accepted ?? 0)}
            tone="good"
            explain={t('home.stat.acceptedProposals.explain')}
          />
          <ExplainedTile
            label={t('home.stat.points')}
            value={formatNumber(stats.data?.points ?? 0)}
            explain={t('home.stat.points.explain')}
          />
        </section>
      )}

      {has.fiveS && stats.data?.mine && <MyFiveSCard mine={stats.data.mine} />}

      {/*
        The ranking, on the screen a worker actually opens. Twelve months, so a
        bad fortnight does not decide somebody's position — and so the figure
        moves slowly enough to be worth acting on.
      */}
      <Leaderboard range={{ from: aYearAgo() }} />

      {tasks.data && tasks.data.data.length > 0 && <MyTasksCard tasks={tasks.data.data} />}

      {has.submissions && (
        <RecentSubmissionsCard
          title={t('home.recent.title')}
          submissions={recent.data?.data ?? []}
          emptyTitle={t('home.recent.empty')}
          emptyDescription={t('home.recent.emptyText')}
        />
      )}
    </div>
  )
}

/**
 * How the centres this person keeps are scoring.
 *
 * The four tiles above are all about reports, which is one third of what
 * somebody does here — a worker who files nothing but keeps two centres
 * immaculate reads as having done nothing at all. This is the other side of
 * that, and it is *their own number*, which is what this screen is for; the
 * plant-wide analytics remain a manager's page.
 *
 * ## Why the plant average is beside it
 *
 * A percentage on its own invites "is that good" and cannot answer it. It is a
 * plant-wide figure, not a ranking — nobody is told their position, and nobody
 * else's name appears.
 *
 * ## Why it is absent rather than zero
 *
 * Somebody who has been on no walk has no figure. A 0% would read as a
 * judgement of them rather than as an absence of data, which is the same
 * distinction the workforce table draws with an em dash.
 */
function MyFiveSCard({ mine }: { mine: MyNumbers }) {
  const { t, tp } = useI18n()

  if (mine.fiveS.walks === 0 || mine.fiveS.percentage === null) return null

  const plant = mine.fiveS.plantPercentage

  return (
    <Card className="flex flex-wrap items-center justify-between gap-4">
      <div>
        <p className="muted inline-flex items-center gap-1.5 text-sm">
          {t('home.fiveS.title')}
          {/* What the second number beside it is for. On a screen that also
              carries a public ranking, a figure next to your own is read as a
              position until something says otherwise. */}
          <Hint>{t('home.fiveS.explain')}</Hint>
        </p>
        <p className="text-3xl font-semibold tabular-nums">
          {formatPercent(mine.fiveS.percentage)}
        </p>
        <p className="muted text-xs">
          {tp('analytics.shifts.walkCount', mine.fiveS.walks)}
          {' · '}
          {tp('analytics.shifts.centreCount', mine.fiveS.centres)}
        </p>
      </div>

      {plant !== null && (
        <div className="text-right">
          <p className="muted text-sm">{t('home.fiveS.plant')}</p>
          <p className="text-xl font-semibold tabular-nums">{formatPercent(plant)}</p>
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Manager / responsible / maintenance / commission / admin
// ---------------------------------------------------------------------------

function ManagerHome({ user }: { user: User }) {
  const { t, tp } = useI18n()
  const has = modulesOf(user)
  const tasks = useMyTasks(true, { enabled: has.submissions })

  // The queue, worst first — that ordering is the entire point of this screen.
  const queue = useSubmissions(
    { openOnly: true, sort: 'priority', perPage: 8 },
    { enabled: has.submissions },
  )
  /*
   * Only `meta.total` is used here; the rows are never rendered.
   *
   * perPage is 5 rather than 1 because the API enforces `min:5` — asking for
   * one row returned 422 on every poll, so the overdue banner silently never
   * appeared. Five is the smallest page the endpoint will issue.
   */
  const overdue = useSubmissions(
    { openOnly: true, overdue: true, perPage: 5 },
    { enabled: has.submissions },
  )

  const openCount = queue.data?.meta.total ?? 0
  const overdueCount = overdue.data?.meta.total ?? 0
  const myTaskCount = tasks.data?.meta.total ?? 0

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">
            {t('home.greeting', { name: user.name.split(' ')[0] })}
          </h1>
          <p className="muted text-sm">{user.roleLabel}</p>
        </div>

        {has.submissions && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" nativeButton={false} render={<Link to="/prijave/nova" />}>
              {t('home.newSubmission')}
            </Button>
            <Button nativeButton={false} render={<Link to="/prijave?otvorene=true" />}>
              {t('home.openQueue')}
            </Button>
          </div>
        )}
      </header>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {has.submissions && (
          <>
            <ExplainedTile
              label={t('home.stat.openSubmissions')}
              value={formatNumber(openCount)}
              hint={t('home.stat.openSubmissions.hint')}
              explain={t('home.stat.openSubmissions.explain')}
            />
            <ExplainedTile
              label={t('home.stat.overdue')}
              value={formatNumber(overdueCount)}
              tone={overdueCount > 0 ? 'bad' : 'good'}
              hint={
                overdueCount > 0
                  ? t('home.stat.overdue.needsAttention')
                  : t('home.stat.overdue.allOnTime')
              }
              explain={t('home.stat.overdue.explain')}
            />
            <ExplainedTile
              label={t('home.stat.myTasks')}
              value={formatNumber(myTaskCount)}
              explain={t('home.stat.myTasks.explain')}
            />
          </>
        )}
        {has.fiveS && (
          <StatTile
            label={t('home.stat.fiveSThisMonth')}
            value={user.canEvaluate ? t('home.stat.fiveS.open') : '—'}
            hint={
              user.canEvaluate ? t('home.stat.fiveS.hint') : t('home.stat.fiveS.notAllowed')
            }
          />
        )}
      </section>

      {/* Overdue work is the one thing that should never need looking for. */}
      {overdueCount > 0 && (
        <Card className="border-rose-300 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/40">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2.5 text-sm font-medium text-rose-900 dark:text-rose-200">
              <AlertTriangle className="size-5 shrink-0" aria-hidden />
              {tp('home.overdue', overdueCount)}
            </p>
            <Button variant="danger" nativeButton={false} render={<Link to="/prijave?overdue=true" />}>
              {t('home.overdue.view')}
            </Button>
          </div>
        </Card>
      )}

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {has.submissions && (
          <>
            <ModuleCard
              to="/prijave?otvorene=true"
              icon={<Inbox className="size-5" />}
              title={t('home.module.queue')}
              text={t('home.module.queue.text')}
            />
            <ModuleCard
              to="/zadaci"
              icon={<ClipboardList className="size-5" />}
              title={t('home.module.tasks')}
              text={t('home.module.tasks.text')}
            />
          </>
        )}
        {has.fiveS && user.canEvaluate && (
          <ModuleCard
            to="/5s"
            icon={<ClipboardCheck className="size-5" />}
            title={t('home.module.fiveS')}
            text={t('home.module.fiveS.text')}
          />
        )}
        {has.analytics && (
          <ModuleCard
            to="/analitika"
            icon={<BarChart3 className="size-5" />}
            title={t('home.module.analytics')}
            text={t('home.module.analytics.text')}
          />
        )}
        {user.role === 'ADMIN' && (
          <ModuleCard
            to="/admin"
            icon={<Settings className="size-5" />}
            title={t('home.module.admin')}
            text={t('home.module.admin.text')}
          />
        )}
      </section>

      {tasks.data && tasks.data.data.length > 0 && <MyTasksCard tasks={tasks.data.data} />}

      {has.submissions && (
        <RecentSubmissionsCard
          title={t('home.queue.title')}
          submissions={queue.data?.data ?? []}
          emptyTitle={t('home.queue.empty')}
          emptyDescription={t('home.queue.emptyText')}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

/**
 * A dashboard tile with one sentence behind a question mark.
 *
 * These seven numbers look like seven counts of the same kind and are not: one
 * is windowed to a year and the rest are since the day somebody started, one is
 * the whole plant's and the rest are personal, and "van roka" is a nought both
 * when nothing is late and when nothing has a deadline. None of that is
 * guessable from a caption two words long.
 *
 * ## Why the icon is laid over the tile
 *
 * `StatTile` takes a plain `label: string` and is shared with the analytics
 * page, admin and the owner console. Widening it to a `ReactNode` to carry a
 * `Hint` would put the affordance on every tile in the application, including
 * the several dozen that need no explaining — so it is added here, on the
 * tiles that need it, and the shared component is left alone.
 *
 * The wrapper is a grid rather than a plain block because these sit in a grid
 * themselves: a block child does not pass its stretched height on to the card
 * inside it, and the row would come out ragged where its neighbours are level.
 *
 * The icon sits *beside* the card rather than inside it, which is also what
 * keeps it visible: the card is `overflow-hidden`. The padding on the label is
 * the other half of that — the caption is the one thing at the height the icon
 * floats at, and "Prihvaćeni prijedlozi" in a half-width column on a phone runs
 * the full width of the tile. Reserving the space is deterministic; checking at
 * one viewport and hoping is not.
 */
function ExplainedTile({
  explain,
  ...tile
}: ComponentProps<typeof StatTile> & { explain: string }) {
  return (
    <div className="relative grid [&_p:first-of-type]:pr-6">
      <StatTile {...tile} />
      {/*
        `side="left"`: the trigger sits against the right edge of a tile that
        is itself against the right edge of the page on a phone, and a tooltip
        opening upwards from there has nowhere to go.
      */}
      <Hint side="left" className="absolute top-4 right-4 sm:top-5 sm:right-5">
        {explain}
      </Hint>
    </div>
  )
}

function MyTasksCard({ tasks }: { tasks: { id: string; title: string; dueDate: string | null; isOverdue: boolean; statusColor: string; statusLabel: string }[] }) {
  const t = useT()

  return (
    <Card>
      <CardTitle
        action={
          <Link
            to="/zadaci"
            // inline-flex + min-h-11: a bare text link is 20px tall, and this
            // is how a worker gets from the dashboard to their work.
            className="text-brand-600 inline-flex min-h-11 items-center text-sm font-medium"
          >
            {t('home.tasks.all')}
          </Link>
        }
      >
        {t('home.tasks.title')}
      </CardTitle>

      <ul className="divide-border divide-y">
        {tasks.map((task) => (
          <li key={task.id} className="flex items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{task.title}</p>
              <p className="muted text-xs">
                {task.dueDate ? t('home.tasks.due', { date: task.dueDate }) : t('home.tasks.noDue')}
              </p>
            </div>
            <Badge color={task.isOverdue ? 'rose' : task.statusColor}>
              {/* `statusLabel` is the server's — see types/api.ts. Only the
                  overdue override is ours. */}
              {task.isOverdue ? t('home.tasks.overdue') : task.statusLabel}
            </Badge>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function RecentSubmissionsCard({
  title,
  submissions,
  emptyTitle,
  emptyDescription,
}: {
  title: string
  submissions: Submission[]
  emptyTitle: string
  emptyDescription: string
}) {
  const t = useT()

  return (
    <Card>
      <CardTitle
        action={
          <Link
            to="/prijave"
            className="text-brand-600 inline-flex min-h-11 items-center text-sm font-medium"
          >
            {t('home.submissions.all')}
          </Link>
        }
      >
        {title}
      </CardTitle>

      {submissions.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <ul className="divide-border divide-y">
          {submissions.map((submission) => (
            <li key={submission.id}>
              <Link
                to={`/prijave/${submission.id}`}
                className="hover:bg-muted -mx-2 flex items-center justify-between gap-3 rounded-xl px-2 py-3 transition-colors"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {submission.title || submission.typeLabel}
                  </p>
                  <p className="muted truncate text-xs">
                    {submission.referenceNo} · {submission.workCenter?.name} ·{' '}
                    {formatRelative(submission.createdAt)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {submission.isOverdue && (
                    <Badge color="rose">{t('home.tasks.overdue')}</Badge>
                  )}
                  <Badge color={submission.statusColor}>{submission.statusLabel}</Badge>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

/** A large, thumb-sized entry point. Used only on the worker dashboard. */
function BigAction({
  to,
  icon,
  title,
  text,
  primary = false,
}: {
  to: string
  icon: React.ReactNode
  title: string
  text: string
  primary?: boolean
}) {
  return (
    <Link
      to={to}
      className={
        primary
          ? 'bg-primary text-primary-foreground flex min-h-28 flex-col justify-center gap-1 rounded-2xl p-5 shadow-sm transition-transform active:scale-[0.99]'
          : 'bg-card hover:bg-muted flex min-h-28 flex-col justify-center gap-1 rounded-2xl border p-5 shadow-sm transition-colors'
      }
    >
      <span className={primary ? '' : 'text-brand-600'}>{icon}</span>
      <span className="mt-1 font-semibold">{title}</span>
      <span className={primary ? 'text-sm opacity-90' : 'muted text-sm'}>{text}</span>
    </Link>
  )
}

function ModuleCard({
  to,
  icon,
  title,
  text,
}: {
  to: string
  icon: React.ReactNode
  title: string
  text: string
}) {
  return (
    <Link
      to={to}
      className="bg-card hover:bg-muted flex flex-col gap-2 rounded-2xl border p-4 shadow-sm transition-colors"
    >
      <span className="text-brand-600">{icon}</span>
      <span className="font-medium">{title}</span>
      <span className="muted text-xs">{text}</span>
    </Link>
  )
}
